import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useStatuses } from '../../../hooks/useStatuses';
import { usePriorities } from '../../../hooks/usePriorities';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { Modal } from '../../../components/ui/Modal';
import QueueConfidentialityLevelControl from '../../../components/tickets/QueueConfidentialityLevelControl';
import { 
  Users, 
  Search, 
  Filter, 
  Clock, 
  User, 
  MessageSquare, 
  CheckCircle, 
  AlertCircle, 
  MoreHorizontal,
  Eye,
  Edit,
  Archive,
  Star,
  Flag,
  Calendar,
  Tag,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Ticket,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  X,
  Download,
  FileText,
  ChevronDown
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ticketService } from '../../../api/ticketService';
import inquiryCategoryMasterService from '../../../api/inquiryCategoryMasterService';
import { getUsersWithRole } from '../../../api/roleManagementService';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { extractErrorMessage } from '../../../utils/errorHandler';
import {
  consumeNotificationTicketSearch,
  consumeNotificationTeamTicketsTab,
} from '../../../utils/notificationNavigation';
import {
  resolveTicketCategoryLabel,
  resolveTicketPriorityLabel as resolveInquiryPriorityLabel,
  resolveInquiryBreadcrumb,
  resolveCurrentSlaWindow,
} from '../../../utils/inquiryDisplay';

const findPriorityByToken = (token, prioritiesList) => {
  if (!token || !Array.isArray(prioritiesList)) return null;
  const normalized = String(token).toLowerCase();
  return (
    prioritiesList.find((p) => {
      const code = (p.priorityCode || '').toLowerCase();
      const name = (p.priorityName || '').toLowerCase();
      return code === normalized || name === normalized;
    }) || null
  );
};

const findPriorityById = (priorityId, prioritiesList) => {
  if (priorityId == null || !Array.isArray(prioritiesList)) return null;
  return prioritiesList.find((p) => p.priorityId === priorityId) || null;
};

const resolveTicketPriorityLabel = (ticket, prioritiesList) => {
  if (ticket?.priorityName) return ticket.priorityName;
  if (ticket?.priority) return ticket.priority;
  const fromId = findPriorityById(ticket?.priorityId, prioritiesList);
  return fromId?.priorityName || fromId?.priorityCode || '—';
};

const getPriorityColorClass = (priorityLabel) => {
  const key = (priorityLabel || '').toLowerCase();
  const colors = {
    critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    urgent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  };
  return colors[key] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
};

const prioritySortKey = (ticket, prioritiesList) => {
  const p =
    findPriorityById(ticket?.priorityId, prioritiesList) ||
    findPriorityByToken(ticket?.priorityCode || ticket?.priority, prioritiesList);
  return p?.displayOrder ?? p?.priorityId ?? 0;
};

const TeamTickets = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('pending-assignments'); // 'team-tickets' or 'pending-assignments' - Default to pending assignments
  const [teamTickets, setTeamTickets] = useState([]);
  const [pendingTickets, setPendingTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [teamAgents, setTeamAgents] = useState([]); // All agents in the team
  const { data: ticketStatuses = [] } = useStatuses();
  const { data: prioritiesRaw = [] } = usePriorities();
  const priorities = useMemo(
    () => prioritiesRaw.filter((p) => p.isActive !== false),
    [prioritiesRaw]
  );
  const assignmentPriorities = useMemo(
    () =>
      priorities.filter((p) => (p.priorityCode || '').toLowerCase() !== 'not_prioritised'),
    [priorities]
  );
  const [inquiryPriorityLevels, setInquiryPriorityLevels] = useState([]);
  const [inquiryCategories, setInquiryCategories] = useState([]);
  const [teamInquiryCategoryFilter, setTeamInquiryCategoryFilter] = useState('all');
  const [teamInquiryPriorityFilter, setTeamInquiryPriorityFilter] = useState('all');
  const [slaTickSeconds, setSlaTickSeconds] = useState(0);
  const ticketsSlaFetchedAtRef = useRef(Date.now());
  const [teamStats, setTeamStats] = useState({
    totalTickets: 0,
    activeTickets: 0,
    resolvedTickets: 0,
    closedTickets: 0
  });
  const [loading, setLoading] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [assigningTicketId, setAssigningTicketId] = useState(null); // Track which ticket is being assigned
  const [assignPriorityModalOpen, setAssignPriorityModalOpen] = useState(false);
  const [assignPriorityTicketId, setAssignPriorityTicketId] = useState(null);
  const [selectedAssignPriorityId, setSelectedAssignPriorityId] = useState('');
  const [assignResponsibleRoleId, setAssignResponsibleRoleId] = useState(null);
  const [assignResponsibleRoleName, setAssignResponsibleRoleName] = useState('');
  const [assignEscalationLevelName, setAssignEscalationLevelName] = useState('');
  const [assignTicket, setAssignTicket] = useState(null);
  const [assignUsers, setAssignUsers] = useState([]);
  const [selectedAssignUserId, setSelectedAssignUserId] = useState('');
  const [assignmentRemarks, setAssignmentRemarks] = useState('');
  const [loadingAssignUsers, setLoadingAssignUsers] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [agentFilter, setAgentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  // Filters for pending assignments
  const [pendingPriorityFilter, setPendingPriorityFilter] = useState('all');
  const [pendingEscalatedByFilter, setPendingEscalatedByFilter] = useState('all');
  const [pendingSlaStatusFilter, setPendingSlaStatusFilter] = useState('all');
  const [pendingSortBy, setPendingSortBy] = useState('created');
  const [currentPage, setCurrentPage] = useState(0);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState({
    pageNumber: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false
  });
  const [pendingPaginationData, setPendingPaginationData] = useState({
    pageNumber: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false
  });
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const activeTabRef = useRef(activeTab);
  const loadTeamTicketsRef = useRef(null);
  const loadPendingAssignmentsRef = useRef(null);
  const loadTeamStatsRef = useRef(null);
  const pendingFiltersRef = useRef(null);
  const teamTicketsBootstrappedRef = useRef(false);

  const applyNotificationTeamTicketsContext = () => {
    const tab = consumeNotificationTeamTicketsTab();
    const search = consumeNotificationTicketSearch();
    if (tab) {
      setActiveTab(tab);
    }
    if (search) {
      setSearchTerm(search);
      if (tab === 'team-tickets') {
        setCurrentPage(0);
      } else if (tab === 'pending-assignments') {
        setPendingCurrentPage(0);
      } else if (activeTabRef.current === 'team-tickets') {
        setCurrentPage(0);
      } else {
        setPendingCurrentPage(0);
      }
    }
    return Boolean(tab || search);
  };

  // Notification click: tab + search patch (same pattern as unassigned queue)
  useEffect(() => {
    applyNotificationTeamTicketsContext();
    teamTicketsBootstrappedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [priorityRows, categoryResp] = await Promise.all([
          ticketService.getInquiryPriorityLevelsForCreate(),
          inquiryCategoryMasterService.getAll(true),
        ]);
        if (cancelled) return;
        setInquiryPriorityLevels(Array.isArray(priorityRows) ? priorityRows : []);
        const catData = categoryResp?.data ?? categoryResp;
        const catRows = Array.isArray(catData) ? catData : (catData?.content || []);
        setInquiryCategories(Array.isArray(catRows) ? catRows : []);
      } catch (e) {
        console.warn('Failed to load inquiry masters for Team Tickets filters', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live SLA tick for current-window display (assigned + pending)
  useEffect(() => {
    const rows = activeTab === 'team-tickets' ? teamTickets : pendingTickets;
    if (!rows?.length) return undefined;
    const interval = setInterval(() => {
      const tick = Math.floor((Date.now() - ticketsSlaFetchedAtRef.current) / 1000);
      setSlaTickSeconds(tick);
    }, 1000);
    return () => clearInterval(interval);
  }, [activeTab, teamTickets, pendingTickets]);

  useEffect(() => {
    if (!teamTicketsBootstrappedRef.current) {
      return undefined;
    }
    if (!applyNotificationTeamTicketsContext()) {
      return undefined;
    }
    return undefined;
  }, [location.key]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Shared data for both tabs (agents, stats) — ticket lists load in dedicated effects below
  useEffect(() => {
    loadTeamAgents();
    loadTeamStats();
  }, [activeTab]);

  // Reset to first page when agent or status filter changes (team tickets)
  useEffect(() => {
    if (
      activeTab === 'team-tickets'
      && (agentFilter !== 'all' || statusFilter !== 'all' || teamInquiryCategoryFilter !== 'all' || teamInquiryPriorityFilter !== 'all')
      && currentPage !== 0
    ) {
      setCurrentPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentFilter, statusFilter, teamInquiryCategoryFilter, teamInquiryPriorityFilter, activeTab]);

  // Team tickets: single debounced fetch (search, page, filters)
  useEffect(() => {
    if (activeTab !== 'team-tickets') return undefined;
    const searchTimeout = setTimeout(() => {
      loadTeamTickets();
    }, searchTerm ? 500 : 0);
    return () => clearTimeout(searchTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, activeTab, agentFilter, statusFilter, teamInquiryCategoryFilter, teamInquiryPriorityFilter]);

  // Pending assignments: single debounced fetch (avoids triple call on mount)
  useEffect(() => {
    if (activeTab !== 'pending-assignments') return undefined;

    const prev = pendingFiltersRef.current;
    const filtersChanged =
      prev != null &&
      (prev.priority !== pendingPriorityFilter ||
        prev.escalated !== pendingEscalatedByFilter ||
        prev.sort !== pendingSortBy);
    pendingFiltersRef.current = {
      priority: pendingPriorityFilter,
      escalated: pendingEscalatedByFilter,
      sort: pendingSortBy,
    };

    if (filtersChanged && pendingCurrentPage !== 0) {
      setPendingCurrentPage(0);
      return undefined;
    }

    const filterTimeout = setTimeout(() => {
      loadPendingAssignments();
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(filterTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    pendingCurrentPage,
    pendingPriorityFilter,
    pendingEscalatedByFilter,
    pendingSortBy,
    searchTerm,
  ]);

  useEffect(() => {
    if (activeTab === 'team-tickets' && teamTickets.length > 0) {
      filterTickets();
    } else if (activeTab === 'pending-assignments' && pendingTickets.length > 0) {
      // Apply client-side SLA status filter only
      const filteredBySla = applySlaStatusFilter(pendingTickets);
      setFilteredTickets(filteredBySla);
    }
  }, [teamTickets, agentFilter, statusFilter, sortBy, activeTab, pendingTickets, pendingSlaStatusFilter, priorities]);

  const loadTeamAgents = async () => {
    try {
      const response = await ticketService.getTeamAgents();
      const responseData = response?.data;
      
      if (responseData?.success && responseData?.data) {
        const agents = Array.isArray(responseData.data) ? responseData.data : [];
        setTeamAgents(agents);
      } else {
        console.warn('Failed to load team agents:', responseData?.message);
        setTeamAgents([]);
      }
    } catch (err) {
      console.error('Error loading team agents:', err);
      setTeamAgents([]);
    }
  };

  const loadTeamStats = async () => {
    try {
      const response = await ticketService.getTeamStats();
      const responseData = response?.data;
      
      if (responseData?.success && responseData?.data) {
        const stats = responseData.data;
        setTeamStats({
          totalTickets: stats.totalTickets || 0,
          activeTickets: stats.activeTickets || 0,
          resolvedTickets: stats.resolvedTickets || 0,
          closedTickets: stats.closedTickets || 0
        });
      } else {
        console.warn('Failed to load team stats:', responseData?.message);
      }
    } catch (err) {
      console.error('Error loading team stats:', err);
    }
  };

  const mapSlaFromTicket = (ticket, tickSeconds = 0) => {
    const window = resolveCurrentSlaWindow(ticket, tickSeconds, ticketStatuses);
    if (window) {
      return {
        slaStatus: window.isOverdue ? 'breached' : 'on-track',
        slaLabel: window.label,
        slaIsOverdue: window.isOverdue,
      };
    }
    return {
      slaStatus: 'on-track',
      slaLabel: '—',
      slaIsOverdue: false,
    };
  };

  const loadTeamTickets = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: currentPage,
        size: pageSize
      };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      if (agentFilter !== 'all') {
        params.assigneeId = parseInt(agentFilter, 10);
      }
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (teamInquiryCategoryFilter !== 'all') {
        const parsed = parseInt(teamInquiryCategoryFilter, 10);
        if (Number.isFinite(parsed)) params.inquiryCategoryId = parsed;
      }
      if (teamInquiryPriorityFilter !== 'all') {
        const parsed = parseInt(teamInquiryPriorityFilter, 10);
        if (Number.isFinite(parsed)) params.inquiryPriorityLevelId = parsed;
      }
      const response = await ticketService.getTeamTickets(params);
      
      const responseData = response?.data;
      
      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        const backendTickets = paginatedData.content || [];
        
        // Transform backend tickets to frontend format
        const transformedTickets = backendTickets.map(ticket => {
          const priorityMeta =
            findPriorityById(ticket.priorityId, priorities) ||
            findPriorityByToken(ticket.priorityCode || ticket.priorityName, priorities);
          const priority =
            resolveInquiryPriorityLabel(ticket) !== '—'
              ? resolveInquiryPriorityLabel(ticket)
              : (ticket.priorityName || priorityMeta?.priorityName || priorityMeta?.priorityCode || '—');
          const category = resolveTicketCategoryLabel(ticket);
          const breadcrumb = resolveInquiryBreadcrumb(ticket);
          const sla = mapSlaFromTicket(ticket, 0);
          
          // Find agent name from teamAgents
          let assignedAgentName = 'Unassigned';
          if (ticket.assigneeId) {
            const agent = teamAgents.find(a => a.userId === ticket.assigneeId);
            if (agent) {
              assignedAgentName = agent.fullName || agent.username || `Agent ${ticket.assigneeId}`;
            } else {
              assignedAgentName = ticket.assigneeFullName || `Agent ${ticket.assigneeId}`;
            }
          }
          
          return {
            id: ticket.ticketId || ticket.ticketNumber,
            ticketId: ticket.ticketId,
            ticketNumber: ticket.ticketNumber,
            title: ticket.subject,
            description: ticket.description || '',
            status: ticket.status?.toLowerCase() || 'open',
            priority,
            priorityId: ticket.priorityId,
            inquiryPriorityLevelId: ticket.inquiryPriorityLevelId,
            inquiryTypeName: ticket.inquiryTypeName,
            inquiryCategoryName: ticket.inquiryCategoryName,
            inquirySubCategoryName: ticket.inquirySubCategoryName,
            inquiryPriorityLevelName: ticket.inquiryPriorityLevelName,
            inquiryBreadcrumb: breadcrumb,
            responsibleRoleId: ticket.responsibleRoleId,
            responsibleRoleName: ticket.responsibleRoleName,
            escalationLevelName: ticket.escalationLevelName,
            priorityCode: (ticket.priorityCode || priorityMeta?.priorityCode || priority || '').toLowerCase(),
            category,
            customer: {
              name: ticket.requesterEmail ? ticket.requesterEmail.split('@')[0] : 'Unknown',
              email: ticket.requesterEmail || '',
              company: ''
            },
            assignedAgent: assignedAgentName,
            assigneeId: ticket.assigneeId,
            createdAt: ticket.createdAt,
            updatedAt: ticket.updatedAt || ticket.createdAt,
            dueDate: ticket.dueDate,
            tags: [],
            attachments: ticket.attachmentCount || 0,
            comments: ticket.replyCount || 0,
            slaStatus: sla.slaStatus,
            slaLabel: sla.slaLabel,
            slaIsOverdue: sla.slaIsOverdue,
            slaRemainingMinutes: ticket.slaRemainingMinutes,
            slaRemainingSeconds: ticket.slaRemainingSeconds,
            slaOverdueMinutes: ticket.slaOverdueMinutes,
            slaOverdueSeconds: ticket.slaOverdueSeconds,
            timeSpent: '0h 0m',
            estimatedTime: '0h',
            lastActivity: ticket.updatedAt || ticket.createdAt,
            agentStatus: 'online',
            workload: 'medium'
          };
        });
        
        ticketsSlaFetchedAtRef.current = Date.now();
        setSlaTickSeconds(0);
        setTeamTickets(transformedTickets);
        setFilteredTickets(transformedTickets);
        
        // Store pagination data
        setPaginationData({
          pageNumber: paginatedData.pageNumber || currentPage,
          pageSize: paginatedData.pageSize || pageSize,
          totalElements: paginatedData.totalElements || 0,
          totalPages: paginatedData.totalPages || 0,
          first: paginatedData.first !== undefined ? paginatedData.first : (currentPage === 0),
          last: paginatedData.last !== undefined ? paginatedData.last : false
        });
      } else {
        setError(responseData?.message || 'Failed to load team tickets');
        toast.error(responseData?.message || 'Failed to load team tickets');
        setTeamTickets([]);
        setFilteredTickets([]);
      }
    } catch (err) {
      console.error('Error loading team tickets:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load team tickets';
      setError(errorMessage);
      toast.error(errorMessage);
      setTeamTickets([]);
      setFilteredTickets([]);
    } finally {
      setLoading(false);
    }
  };
  

  const filterTickets = () => {
    let filtered = [...teamTickets];

    // Search, agent filter, and status filter are now handled server-side, so no client-side filtering needed for those

    // Sort tickets
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'updated':
          return new Date(b.updatedAt) - new Date(a.updatedAt);
        case 'priority':
          return prioritySortKey(b, priorities) - prioritySortKey(a, priorities);
        case 'agent':
          return a.assignedAgent.localeCompare(b.assignedAgent);
        default:
          return 0;
      }
    });

    setFilteredTickets(filtered);
  };

  // Apply client-side SLA status filter only (since SLA is computed dynamically)
  const applySlaStatusFilter = (tickets) => {
    if (!Array.isArray(tickets)) {
      return [];
    }
    if (pendingSlaStatusFilter === 'all') {
      return tickets;
    }
    return tickets.filter(ticket => ticket.slaStatus === pendingSlaStatusFilter);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'in-progress':
        return <Edit className="w-4 h-4 text-blue-500" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'escalated':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Users className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      'in-progress': 'default',
      resolved: 'success',
      escalated: 'destructive'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status.replace('-', ' ')}</Badge>;
  };

  const getAgentStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'text-green-600';
      case 'busy':
        return 'text-yellow-600';
      case 'offline':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const getWorkloadBadge = (workload) => {
    const variants = {
      high: 'destructive',
      medium: 'warning',
      low: 'success'
    };
    return <Badge variant={variants[workload] || 'secondary'}>{workload}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diff = due - now;
    
    if (diff < 0) return 'Overdue';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const loadPendingAssignments = async () => {
    setPendingLoading(true);
    setError('');
    try {
      let priorityId = null;
      if (pendingPriorityFilter !== 'all') {
        const parsed = parseInt(pendingPriorityFilter, 10);
        priorityId = Number.isFinite(parsed) ? parsed : null;
      }
      
      // Map escalatedByAgentId
      let escalatedByAgentId = null;
      if (pendingEscalatedByFilter !== 'all') {
        escalatedByAgentId = parseInt(pendingEscalatedByFilter, 10);
      }
      
      const params = {
        page: pendingCurrentPage,
        size: pageSize,
        priorityId: priorityId,
        escalatedByAgentId: escalatedByAgentId,
        search: searchTerm.trim() || null,
        sortBy: pendingSortBy
      };
      const response = await ticketService.getPendingTeamLeadAssignments(params);
      
      const responseData = response?.data;
      
      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        const backendTickets = paginatedData.content || [];
        
        // Transform backend tickets to frontend format
        const transformedTickets = backendTickets.map(ticket => {
          const priority =
            ticket.inquiryPriorityLevelName
            || ticket.priorityName
            || '—';
          const category = resolveTicketCategoryLabel(ticket);
          const breadcrumb = resolveInquiryBreadcrumb(ticket);
          const sla = mapSlaFromTicket(ticket, 0);
          
          return {
            id: ticket.ticketId || ticket.ticketNumber,
            ticketId: ticket.ticketId,
            ticketNumber: ticket.ticketNumber,
            title: ticket.subject,
            description: ticket.description || '',
            status: ticket.status?.toLowerCase() || 'open',
            priority,
            priorityId: ticket.priorityId,
            inquiryPriorityLevelId: ticket.inquiryPriorityLevelId,
            inquiryTypeName: ticket.inquiryTypeName,
            inquiryCategoryName: ticket.inquiryCategoryName,
            inquirySubCategoryName: ticket.inquirySubCategoryName,
            inquiryPriorityLevelName: ticket.inquiryPriorityLevelName,
            inquiryBreadcrumb: breadcrumb,
            responsibleRoleId: ticket.responsibleRoleId,
            responsibleRoleName: ticket.responsibleRoleName,
            escalationLevelName: ticket.escalationLevelName,
            priorityCode: (ticket.priorityCode || priority || '').toLowerCase(),
            category,
            customer: {
              name: ticket.requesterEmail ? ticket.requesterEmail.split('@')[0] : 'Unknown',
              email: ticket.requesterEmail || '',
              company: ''
            },
            assignedAgent: 'Unassigned',
            assigneeId: null,
            escalatedByAgent: ticket.escalatedByAgentName || ticket.escalatedByAgent || null,
            escalatedByAgentId: ticket.escalatedByAgentId || null,
            createdAt: ticket.createdAt,
            updatedAt: ticket.updatedAt || ticket.createdAt,
            dueDate: ticket.dueDate,
            tags: [],
            attachments: ticket.attachmentCount || 0,
            comments: ticket.replyCount || 0,
            slaStatus: sla.slaStatus,
            slaLabel: sla.slaLabel,
            slaIsOverdue: sla.slaIsOverdue,
            slaRemainingMinutes: ticket.slaRemainingMinutes,
            slaRemainingSeconds: ticket.slaRemainingSeconds,
            slaOverdueMinutes: ticket.slaOverdueMinutes,
            slaOverdueSeconds: ticket.slaOverdueSeconds,
            timeSpent: '0h 0m',
            estimatedTime: '0h',
            lastActivity: ticket.updatedAt || ticket.createdAt,
            agentStatus: 'online',
            workload: 'medium',
            confidentialityLevel: ticket.confidentialityLevel,
          };
        });
        
        ticketsSlaFetchedAtRef.current = Date.now();
        setSlaTickSeconds(0);
        setPendingTickets(transformedTickets);
        // Apply client-side SLA status filter only (other filters are server-side)
        const filteredBySla = applySlaStatusFilter(transformedTickets);
        setFilteredTickets(filteredBySla);
        
        // Store pagination data
        setPendingPaginationData({
          pageNumber: paginatedData.pageNumber || pendingCurrentPage,
          pageSize: paginatedData.pageSize || pageSize,
          totalElements: paginatedData.totalElements || 0,
          totalPages: paginatedData.totalPages || 0,
          first: paginatedData.first !== undefined ? paginatedData.first : (pendingCurrentPage === 0),
          last: paginatedData.last !== undefined ? paginatedData.last : false
        });
      } else {
        setError(responseData?.message || 'Failed to load pending assignments');
        toast.error(responseData?.message || 'Failed to load pending assignments');
        setPendingTickets([]);
        setFilteredTickets([]);
      }
    } catch (err) {
      console.error('Error loading pending assignments:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load pending assignments';
      setError(errorMessage);
      toast.error(errorMessage);
      setPendingTickets([]);
      setFilteredTickets([]);
    } finally {
      setPendingLoading(false);
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'team-tickets') {
      loadTeamTickets();
      loadTeamStats();
      toast.success('Refreshing team tickets...');
    } else {
      loadPendingAssignments();
      toast.success('Refreshing pending assignments...');
    }
  };

  loadTeamTicketsRef.current = loadTeamTickets;
  loadPendingAssignmentsRef.current = loadPendingAssignments;
  loadTeamStatsRef.current = loadTeamStats;

  useEffect(() => {
    const onRealtimeRefresh = () => {
      loadTeamStatsRef.current?.();
      if (activeTabRef.current === 'team-tickets') {
        loadTeamTicketsRef.current?.();
      } else {
        loadPendingAssignmentsRef.current?.();
      }
    };
    window.addEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
    return () => window.removeEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
  }, []);

  const assignmentInquiryPriorities = useMemo(
    () =>
      (inquiryPriorityLevels || []).filter((p) => {
        const code = String(p.priorityLevelCode || p.code || '').toLowerCase().replace(/[\s-]+/g, '_');
        const name = String(p.priorityLevelName || p.name || '').toLowerCase();
        return code !== 'not_prioritised' && name !== 'not prioritised';
      }),
    [inquiryPriorityLevels]
  );

  const loadAssignUsersForRole = async (roleId) => {
    if (!roleId) {
      setAssignUsers([]);
      return;
    }
    setLoadingAssignUsers(true);
    try {
      const response = await getUsersWithRole(roleId);
      const payload = response?.data?.data ?? response?.data ?? response;
      const users = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.content) ? payload.content : []);
      setAssignUsers(users);
      if (users.length === 0) {
        toast.error('No users found for the Responsible Role');
      }
    } catch (e) {
      console.error('Failed to load users for responsible role', e);
      toast.error(extractErrorMessage(e, 'Failed to load users for Responsible Role'));
      setAssignUsers([]);
    } finally {
      setLoadingAssignUsers(false);
    }
  };

  const handleAssignPriorityChange = async (priorityLevelId) => {
    setSelectedAssignPriorityId(priorityLevelId ? String(priorityLevelId) : '');
    setSelectedAssignUserId('');
    setAssignUsers([]);
    setAssignResponsibleRoleId(null);
    setAssignResponsibleRoleName('');
    setAssignEscalationLevelName('');
    if (!priorityLevelId || !assignTicket) return;

    const categoryId = assignTicket.inquiryCategoryId;
    const subCategoryId = assignTicket.inquirySubCategoryId;
    if (!categoryId || !subCategoryId) {
      toast.error('Inquiry Category / Sub Category missing on this ticket');
      return;
    }
    try {
      setLoadingAssignUsers(true);
      const role = await ticketService.getLevel1ResponsibleRoleForAssign(
        categoryId,
        subCategoryId,
        Number(priorityLevelId)
      );
      const roleId = role?.responsibleRoleId;
      setAssignResponsibleRoleId(roleId || null);
      setAssignResponsibleRoleName(role?.responsibleRoleName || '—');
      setAssignEscalationLevelName(role?.escalationLevelName || 'Level 1');
      if (!roleId) {
        toast.error('No Level 1 Responsible Role found for this Inquiry Priority. Check Escalation Matrix.');
        setLoadingAssignUsers(false);
        return;
      }
      await loadAssignUsersForRole(roleId);
    } catch (e) {
      console.error('Failed to resolve Level 1 role for priority', e);
      toast.error(extractErrorMessage(e, 'No Level 1 Responsible Role for this Inquiry Priority'));
      setLoadingAssignUsers(false);
    }
  };

  const openAssignPriorityModal = async (ticket) => {
    const ticketId = ticket?.ticketId || ticket?.id;
    if (!ticketId || assigningTicketId) return;
    setAssignPriorityTicketId(ticketId);
    setAssignTicket(ticket);
    setSelectedAssignUserId('');
    setAssignmentRemarks('');
    setAssignUsers([]);
    setSelectedAssignPriorityId('');
    setAssignResponsibleRoleId(null);
    setAssignResponsibleRoleName('');
    setAssignEscalationLevelName('');
    setAssignPriorityModalOpen(true);
  };

  const closeAssignPriorityModal = () => {
    if (assigningTicketId) return;
    setAssignPriorityModalOpen(false);
    setAssignPriorityTicketId(null);
    setAssignTicket(null);
    setSelectedAssignUserId('');
    setAssignmentRemarks('');
    setAssignUsers([]);
    setSelectedAssignPriorityId('');
    setAssignResponsibleRoleId(null);
    setAssignResponsibleRoleName('');
    setAssignEscalationLevelName('');
  };

  const handleAssignInquiry = async () => {
    const ticketId = assignPriorityTicketId;
    if (!ticketId) {
      toast.error('Invalid ticket ID');
      return;
    }
    if (!selectedAssignPriorityId) {
      toast.error('Please select an Inquiry Priority Level');
      return;
    }
    if (!selectedAssignUserId) {
      toast.error('Please select a user');
      return;
    }
    if (!assignmentRemarks.trim()) {
      toast.error('Assignment remarks are required');
      return;
    }
    if (assigningTicketId) return;

    try {
      setAssigningTicketId(ticketId);
      const assignResponse = await ticketService.assignTicket(
        ticketId,
        Number(selectedAssignUserId),
        null,
        Number(selectedAssignPriorityId)
      );
      const responseData = assignResponse?.data;
      if (!(responseData?.success || responseData?.status === 200)) {
        toast.error(responseData?.message || 'Failed to assign inquiry');
        return;
      }

      try {
        await ticketService.addReply(ticketId, {
          content: `Assignment remarks: ${assignmentRemarks.trim()}`,
          replyType: 'assignment',
          isInternal: true
        });
      } catch (noteErr) {
        console.warn('Assignment succeeded but remarks note failed', noteErr);
      }

      toast.success('Inquiry assigned successfully');
      setAssigningTicketId(null);
      closeAssignPriorityModal();
      await loadPendingAssignments();
    } catch (error) {
      console.error('Error assigning inquiry:', error);
      if (error?.status === 409) {
        toast.error(
          error.message ||
            'This inquiry is already assigned to another user.'
        );
      } else {
        toast.error(
          error.response?.data?.message || error.message || 'Error assigning inquiry. Please try again.'
        );
      }
    } finally {
      setAssigningTicketId(null);
    }
  };

  const handleAssignToMe = async (ticketId, priorityId) => {
    // Legacy path retained for non-inquiry flows; Inquiry uses handleAssignInquiry.
    if (!ticketId) {
      toast.error('Invalid ticket ID');
      console.error('Ticket ID is missing');
      return;
    }
    if (!priorityId) {
      toast.error('Please select a priority');
      return;
    }

    // Prevent multiple simultaneous assignments
    if (assigningTicketId) {
      return;
    }

    try {
      setAssigningTicketId(ticketId);
      
      // Get current user ID from sessionStorage (consistent with other components)
      const userDetailsStr = sessionStorage.getItem('userDetails');
      const userIdStr = sessionStorage.getItem('userId');
      
      let currentUserId = null;
      
      // Try to get from userDetails first
      if (userDetailsStr) {
        try {
          const userDetails = JSON.parse(userDetailsStr);
          currentUserId = userDetails?.userId || userDetails?.id;
        } catch (e) {
          console.warn('Failed to parse userDetails:', e);
        }
      }
      
      // Fallback to direct userId from sessionStorage
      if (!currentUserId && userIdStr) {
        currentUserId = parseInt(userIdStr, 10);
      }
      
      // Last resort: try sessionStorage
      if (!currentUserId) {
        const userStr = sessionStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            currentUserId = user?.userId || user?.id;
          } catch (e) {
            console.warn('Failed to parse user from localStorage:', e);
          }
        }
      }
      
      if (!currentUserId) {
        toast.error('Unable to determine user ID. Please log in again.');
        console.error('User ID not found in sessionStorage or localStorage');
        setAssigningTicketId(null);
        return;
      }
      
      const requesterId = null; // Not needed for team lead assignment
      const assignResponse = await ticketService.assignTicket(
        ticketId,
        currentUserId,
        requesterId,
        Number(priorityId)
      );
      
      const responseData = assignResponse?.data;
      
      if (responseData?.success || responseData?.status === 200) {
        toast.success('Ticket assigned to you successfully');
        setAssignPriorityModalOpen(false);
        setAssignPriorityTicketId(null);
        setSelectedAssignPriorityId('');
        // Refresh pending assignments
        await loadPendingAssignments();
      } else {
        toast.error(responseData?.message || 'Failed to assign ticket');
        console.error('Assign ticket response:', responseData);
      }
    } catch (error) {
      console.error('Error assigning ticket:', error);
      if (error?.status === 409) {
        toast.error(
          error.message ||
            'This ticket is already assigned to another agent. Someone may have claimed it just before you.'
        );
      } else {
        const errorMessage =
          error.response?.data?.message || error.message || 'Error assigning ticket. Please try again.';
        toast.error(errorMessage);
      }

      if (error.response) {
        console.error('Error response:', error.response.data);
      }
    } finally {
      setAssigningTicketId(null);
    }
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleClearFilters = () => {
    if (activeTab === 'team-tickets') {
      setAgentFilter('all');
      setStatusFilter('all');
      setTeamInquiryCategoryFilter('all');
      setTeamInquiryPriorityFilter('all');
      setSortBy('updated');
      setSearchTerm('');
    } else if (activeTab === 'pending-assignments') {
      setPendingPriorityFilter('all');
      setPendingEscalatedByFilter('all');
      setPendingSlaStatusFilter('all');
      setPendingSortBy('created');
      setSearchTerm('');
    }
  };

  const formatDateTimeExport = (isoString) => {
    if (!isoString) return '-';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '-';
    }
  };

  const escapeCsvCell = (val) => {
    if (val == null || val === '') return '';
    const s = String(val);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const fetchTeamTicketsForExport = async () => {
    const params = { page: 0, size: 1000 };
    if (searchTerm?.trim()) params.search = searchTerm.trim();
    if (agentFilter !== 'all') params.assigneeId = parseInt(agentFilter, 10);
    if (statusFilter !== 'all') params.status = statusFilter;
    if (teamInquiryCategoryFilter !== 'all') {
      const parsed = parseInt(teamInquiryCategoryFilter, 10);
      if (Number.isFinite(parsed)) params.inquiryCategoryId = parsed;
    }
    if (teamInquiryPriorityFilter !== 'all') {
      const parsed = parseInt(teamInquiryPriorityFilter, 10);
      if (Number.isFinite(parsed)) params.inquiryPriorityLevelId = parsed;
    }
    const response = await ticketService.getTeamTickets(params);
    const content = response?.data?.data?.content || [];
    return (content || []).map((ticket) => {
      const priority = resolveInquiryPriorityLabel(ticket) !== '—'
        ? resolveInquiryPriorityLabel(ticket)
        : resolveTicketPriorityLabel(ticket, priorities);
      let assignedAgentName = 'Unassigned';
      if (ticket.assigneeId) {
        const agent = teamAgents.find(a => a.userId === ticket.assigneeId);
        assignedAgentName = agent ? (agent.fullName || agent.username || `Agent ${ticket.assigneeId}`) : (ticket.assigneeFullName || `Agent ${ticket.assigneeId}`);
      }
      const sla = mapSlaFromTicket(ticket, 0);
      return {
        ticketNumber: ticket.ticketNumber || ticket.ticketId || 'N/A',
        subject: ticket.subject || '',
        inquiryType: ticket.inquiryTypeName || '—',
        inquiryCategory: resolveTicketCategoryLabel(ticket),
        inquirySubCategory: ticket.inquirySubCategoryName || '—',
        inquiryPriority: ticket.inquiryPriorityLevelName || priority,
        responsibleRole: ticket.responsibleRoleName || '—',
        escalationLevel: ticket.escalationLevelName || '—',
        category: resolveTicketCategoryLabel(ticket),
        priority,
        status: (ticket.status || 'open').toLowerCase(),
        assignedAgent: assignedAgentName,
        slaStatus: sla.slaLabel,
        createdAt: ticket.createdAt
      };
    });
  };

  const fetchPendingForExport = async () => {
    let priorityId = null;
    if (pendingPriorityFilter !== 'all') {
      const parsed = parseInt(pendingPriorityFilter, 10);
      priorityId = Number.isFinite(parsed) ? parsed : null;
    }
    const params = {
      page: 0,
      size: 1000,
      priorityId: priorityId || undefined,
      escalatedByAgentId: pendingEscalatedByFilter !== 'all' ? parseInt(pendingEscalatedByFilter, 10) : undefined,
      search: searchTerm?.trim() || undefined,
      sortBy: pendingSortBy
    };
    const response = await ticketService.getPendingTeamLeadAssignments(params);
    const content = response?.data?.data?.content || [];
    return (content || []).map((ticket) => {
      const sla = mapSlaFromTicket(ticket, 0);
      return {
        ticketNumber: ticket.ticketNumber || ticket.ticketId || 'N/A',
        subject: ticket.subject || '',
        inquiryType: ticket.inquiryTypeName || '—',
        inquiryCategory: resolveTicketCategoryLabel(ticket),
        inquirySubCategory: ticket.inquirySubCategoryName || '—',
        inquiryPriority: ticket.inquiryPriorityLevelName || ticket.priorityName || '—',
        responsibleRole: ticket.responsibleRoleName || '—',
        escalationLevel: ticket.escalationLevelName || '—',
        priority: ticket.inquiryPriorityLevelName || ticket.priorityName || '—',
        status: (ticket.status || 'open').toLowerCase(),
        escalatedBy: ticket.responsibleRoleName || ticket.escalatedByAgentName || '—',
        slaStatus: sla.slaLabel,
        createdAt: ticket.createdAt
      };
    });
  };

  const handleExportCsv = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const isTeamTickets = activeTab === 'team-tickets';
      const rows = isTeamTickets ? await fetchTeamTicketsForExport() : await fetchPendingForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const isTeam = activeTab === 'team-tickets';
      const headers = isTeam
        ? ['S.NO', 'Ticket Number', 'Subject', 'Inquiry Type', 'Inquiry Category', 'Inquiry Sub Category', 'Inquiry Priority', 'Responsible Role', 'Escalation Level', 'Status', 'Assigned Agent', 'SLA', 'Created']
        : ['S.NO', 'Ticket Number', 'Subject', 'Inquiry Type', 'Inquiry Category', 'Inquiry Priority', 'Responsible Role', 'Escalation Level', 'Status', 'SLA', 'Created'];
      const csvRows = rows.map((r, i) => {
        const row = isTeam
          ? [i + 1, r.ticketNumber, r.subject, r.inquiryType, r.inquiryCategory, r.inquirySubCategory, r.inquiryPriority, r.responsibleRole, r.escalationLevel, r.status, r.assignedAgent, r.slaStatus, formatDateTimeExport(r.createdAt)]
          : [i + 1, r.ticketNumber, r.subject, r.inquiryType, r.inquiryCategory, r.inquiryPriority, r.responsibleRole, r.escalationLevel, r.status, r.slaStatus, formatDateTimeExport(r.createdAt)];
        return row.map(escapeCsvCell);
      });
      const csv = [headers.map(escapeCsvCell).join(','), ...csvRows.map((r) => r.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `team-tickets-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Tickets exported as CSV');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const isTeamTickets = activeTab === 'team-tickets';
      const rows = isTeamTickets ? await fetchTeamTicketsForExport() : await fetchPendingForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const isTeam = activeTab === 'team-tickets';
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text(isTeam ? 'Team Tickets' : 'Pending Assignments', 14, 15);
      doc.setFontSize(10);
      const head = isTeam
        ? [['S.NO', 'Ticket', 'Subject', 'Category', 'Priority', 'Role', 'Level', 'Status', 'Assignee', 'SLA']]
        : [['S.NO', 'Ticket', 'Subject', 'Priority', 'Role', 'Level', 'Status', 'SLA']];
      const body = rows.map((r, i) =>
        isTeam
          ? [i + 1, r.ticketNumber, (r.subject || '').substring(0, 24), (r.inquiryCategory || '').substring(0, 12), (r.inquiryPriority || '').substring(0, 10), (r.responsibleRole || '').substring(0, 12), (r.escalationLevel || '').substring(0, 10), r.status, (r.assignedAgent || '').substring(0, 12), (r.slaStatus || '').substring(0, 14)]
          : [i + 1, r.ticketNumber, (r.subject || '').substring(0, 28), (r.inquiryPriority || '').substring(0, 10), (r.responsibleRole || '').substring(0, 14), (r.escalationLevel || '').substring(0, 10), r.status, (r.slaStatus || '').substring(0, 14)]
      );
      autoTable(doc, { startY: 22, head, body, styles: { fontSize: 7 } });
      doc.save(`team-tickets-${activeTab}-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Tickets exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleViewTicket = (ticketId) => {
    navigate(`/agent/tickets/${ticketId}`, { state: { mode: 'view', openComments: true, scrollToComments: true } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Team Tickets</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {activeTab === 'team-tickets' 
              ? 'Overview of all team tickets and agent workload distribution'
              : 'Open unassigned inquiries waiting for Help Desk Officer (TEAM_LEAD) assignment'}
          </p>
        </div>
        <div className="flex gap-3 flex-nowrap">
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={handleRefresh}
            disabled={activeTab === 'team-tickets' ? loading : pendingLoading}
          >
            <RefreshCw className={`w-4 h-4 ${(activeTab === 'team-tickets' ? loading : pendingLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <div className="relative flex-shrink-0">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                setExportMenuOpen((v) => !v);
              }}
              disabled={(activeTab === 'team-tickets' ? loading : pendingLoading) || exporting}
            >
              <Download className="h-4 w-4" />
              <span>{exporting ? 'Exporting…' : 'Export'}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
            {exportMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" aria-hidden onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 py-1 z-50 min-w-[140px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportCsv}
                  >
                    <FileText className="h-4 w-4" />
                    Export CSV
                  </button>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportPdf}
                  >
                    <FileText className="h-4 w-4" />
                    Export PDF
                  </button>
                </div>
              </>
            )}
          </div>
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={handleToggleFilters}
          >
            <Filter className="w-4 h-4" />
            Filters
          </Button>
          {showFilters && (
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={handleClearFilters}
            >
              <X className="w-4 h-4" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          
          <button
            onClick={() => {
              setActiveTab('pending-assignments');
              setPendingCurrentPage(0);
              setSearchTerm('');
              // Reset pending assignment filters when switching to this tab
              setPendingPriorityFilter('all');
              setPendingEscalatedByFilter('all');
              setPendingSlaStatusFilter('all');
              setPendingSortBy('created');
            }}
            className={`${
              activeTab === 'pending-assignments'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
          >
            <UserPlus className="w-4 h-4" />
            Pending Assignments
          </button>
          <button
            onClick={() => {
              setActiveTab('team-tickets');
              setCurrentPage(0);
              setSearchTerm('');
              // Reset team tickets filters when switching to this tab
              setAgentFilter('all');
              setStatusFilter('all');
              setTeamInquiryCategoryFilter('all');
              setTeamInquiryPriorityFilter('all');
              setSortBy('updated');
            }}
            className={`${
              activeTab === 'team-tickets'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
          >
            <Users className="w-4 h-4" />
            Team Tickets
          </button>
        </nav>
      </div>

      {/* Team Statistics - Only show for team-tickets tab */}
      {activeTab === 'team-tickets' && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tickets</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{teamStats?.totalTickets || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{teamStats?.activeTickets || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Resolved</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{teamStats?.resolvedTickets || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Archive className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Closed</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{teamStats?.closedTickets || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Search Bar - Always visible */}
      <Card>
        <CardContent className="p-6">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Input
                placeholder={activeTab === 'team-tickets' ? "Search team tickets..." : "Search pending assignments..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            {/* Filter Dropdowns - Show when showFilters is true */}
            {showFilters && activeTab === 'team-tickets' && (
              <>
                <select
                  value={agentFilter}
                  onChange={(e) => setAgentFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Agents</option>
                  {teamAgents.map((agent) => (
                    <option key={agent.userId} value={agent.userId.toString()}>
                      {agent.fullName || agent.username || `Agent ${agent.userId}`}
                    </option>
                  ))}
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Status</option>
                  {Array.isArray(ticketStatuses) && ticketStatuses.length > 0 ? (
                    ticketStatuses
                      .filter(status => status.statusCode && status.statusName) // Filter out invalid statuses
                      .sort((a, b) => {
                        // Sort by statusName for better UX
                        const nameA = (a.statusName || '').toLowerCase();
                        const nameB = (b.statusName || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((status) => (
                        <option key={status.statusId || status.id} value={status.statusCode}>
                          {status.statusName || status.statusCode}
                        </option>
                      ))
                  ) : null}
                </select>
                <select
                  value={teamInquiryCategoryFilter}
                  onChange={(e) => setTeamInquiryCategoryFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Inquiry Categories</option>
                  {inquiryCategories.map((cat) => {
                    const id = cat.id || cat.inquiryCategoryId;
                    return (
                      <option key={id} value={String(id)}>
                        {cat.inquiryCategoryName || cat.name || `Category ${id}`}
                      </option>
                    );
                  })}
                </select>
                <select
                  value={teamInquiryPriorityFilter}
                  onChange={(e) => setTeamInquiryPriorityFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Inquiry Priorities</option>
                  {inquiryPriorityLevels.map((priority) => (
                    <option
                      key={priority.id || priority.inquiryPriorityLevelId}
                      value={String(priority.id || priority.inquiryPriorityLevelId)}
                    >
                      {priority.priorityLevelName || priority.name}
                    </option>
                  ))}
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="updated">Last Updated</option>
                  <option value="created">Created Date</option>
                  <option value="priority">Priority</option>
                  <option value="agent">Agent</option>
                </select>
              </>
            )}
            {showFilters && activeTab === 'pending-assignments' && (
              <>
                <select
                  value={pendingPriorityFilter}
                  onChange={(e) => setPendingPriorityFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Priority Levels</option>
                  {inquiryPriorityLevels.map((priority) => (
                    <option
                      key={priority.id || priority.inquiryPriorityLevelId}
                      value={String(priority.id || priority.inquiryPriorityLevelId)}
                    >
                      {priority.priorityLevelName || priority.name}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      {((activeTab === 'team-tickets' && !loading) || (activeTab === 'pending-assignments' && !pendingLoading)) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  {activeTab === 'team-tickets' && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Escalation Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Responsible Role
                      </th>
                    </>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {activeTab === 'pending-assignments' ? 'Responsible Role' : 'Assignee'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    SLA Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTickets.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'team-tickets' ? '10' : '8'} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>{activeTab === 'pending-assignments' ? 'No pending assignments found' : 'No team tickets found'}</p>
                      <p className="text-sm mt-2">
                        {activeTab === 'pending-assignments' 
                          ? 'All escalated tickets have been assigned'
                          : 'Try adjusting your search criteria'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredTickets.map((ticket, index) => {
                    const serialNumber = (activeTab === 'team-tickets' ? currentPage : pendingCurrentPage) * pageSize + index + 1;
                    const priorityLabel = ticket.priority || '—';
                    const liveSla = resolveCurrentSlaWindow(ticket, slaTickSeconds, ticketStatuses);
                    const slaLabel = liveSla?.label || ticket.slaLabel || '—';
                    const slaOverdue = liveSla?.isOverdue ?? ticket.slaIsOverdue;
                    const getStatusColor = (s) => {
                      const colors = {
                        open: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                        'in-progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                        resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                        closed: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
                        escalated: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      };
                      return colors[s] || colors.open;
                    };
                    return (
                      <tr key={ticket.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {serialNumber}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                <Ticket className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {ticket.ticketNumber || ticket.id}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                {ticket.title || ticket.subject}
                              </div>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs" title={ticket.inquiryBreadcrumb || ticket.category}>
                                  {ticket.inquiryBreadcrumb || ticket.category || '—'}
                                </span>
                                {ticket.attachments > 0 && (
                                  <div className="flex items-center space-x-1">
                                    <Tag className="h-3 w-3 text-gray-400" />
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {ticket.attachments}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={getPriorityColorClass(priorityLabel)}>
                            {priorityLabel}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={getStatusColor(ticket.status)}>
                            {ticket.status}
                          </Badge>
                        </td>
                        {activeTab === 'team-tickets' && (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {ticket.escalationLevelName || '—'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {ticket.responsibleRoleName || '—'}
                              </div>
                            </td>
                          </>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {activeTab === 'pending-assignments' 
                              ? (ticket.responsibleRoleName || '—')
                              : (ticket.assignedAgent || 'Unassigned')}
                          </div>
                          {activeTab === 'pending-assignments' && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {ticket.escalationLevelName || 'Level 1'} · Waiting for assignment
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm ${
                            slaOverdue ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                          }`}>
                            {slaLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(ticket.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewTicket(ticket.ticketId || ticket.id)}
                              className="flex items-center gap-1"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </Button>
                            {activeTab === 'pending-assignments' && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => openAssignPriorityModal(ticket)}
                                disabled={assigningTicketId === (ticket.ticketId || ticket.id) || !!assigningTicketId}
                                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {assigningTicketId === (ticket.ticketId || ticket.id) ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Assigning...
                                  </>
                                ) : (
                                  <>
                                    <UserPlus className="w-4 h-4" />
                                    Assign Inquiry
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {((activeTab === 'team-tickets' && paginationData && paginationData.totalPages > 1) ||
            (activeTab === 'pending-assignments' && pendingPaginationData && pendingPaginationData.totalPages > 1)) && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
              {activeTab === 'team-tickets' ? (
                <>
                  <div className="flex-1 flex justify-between sm:hidden gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={paginationData.first}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.min(paginationData.totalPages - 1, currentPage + 1))}
                      disabled={paginationData.last}
                    >
                      Next
                    </Button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Showing <span className="font-medium">{currentPage * pageSize + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min((currentPage + 1) * pageSize, paginationData.totalElements)}
                        </span>{' '}
                        of <span className="font-medium">{paginationData.totalElements}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm gap-2" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                          disabled={currentPage === 0 || paginationData.first}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        
                        {/* Page Numbers */}
                        {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                          let pageNum;
                          if (paginationData.totalPages <= 5) {
                            pageNum = i;
                          } else if (currentPage < 3) {
                            pageNum = i;
                          } else if (currentPage > paginationData.totalPages - 4) {
                            pageNum = paginationData.totalPages - 5 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-1 text-sm border rounded-md ${
                                pageNum === currentPage
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              {pageNum + 1}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => setCurrentPage(Math.min(paginationData.totalPages - 1, currentPage + 1))}
                          disabled={currentPage >= paginationData.totalPages - 1 || paginationData.last}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-1 flex justify-between sm:hidden gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPendingCurrentPage(Math.max(0, pendingCurrentPage - 1))}
                      disabled={pendingPaginationData.first}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPendingCurrentPage(Math.min(pendingPaginationData.totalPages - 1, pendingCurrentPage + 1))}
                      disabled={pendingPaginationData.last}
                    >
                      Next
                    </Button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Showing <span className="font-medium">{pendingCurrentPage * pageSize + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min((pendingCurrentPage + 1) * pageSize, pendingPaginationData.totalElements)}
                        </span>{' '}
                        of <span className="font-medium">{pendingPaginationData.totalElements}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm gap-2" aria-label="Pagination">
                        <button
                          onClick={() => setPendingCurrentPage(Math.max(0, pendingCurrentPage - 1))}
                          disabled={pendingCurrentPage === 0 || pendingPaginationData.first}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        
                        {/* Page Numbers */}
                        {Array.from({ length: Math.min(5, pendingPaginationData.totalPages) }, (_, i) => {
                          let pageNum;
                          if (pendingPaginationData.totalPages <= 5) {
                            pageNum = i;
                          } else if (pendingCurrentPage < 3) {
                            pageNum = i;
                          } else if (pendingCurrentPage > pendingPaginationData.totalPages - 4) {
                            pageNum = pendingPaginationData.totalPages - 5 + i;
                          } else {
                            pageNum = pendingCurrentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPendingCurrentPage(pageNum)}
                              className={`px-3 py-1 text-sm border rounded-md ${
                                pageNum === pendingCurrentPage
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              {pageNum + 1}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => setPendingCurrentPage(Math.min(pendingPaginationData.totalPages - 1, pendingCurrentPage + 1))}
                          disabled={pendingCurrentPage >= pendingPaginationData.totalPages - 1 || pendingPaginationData.last}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="w-4 h-4" />
          <div>
            <h4 className="font-semibold">Error loading team tickets</h4>
            <p className="text-sm">{error}</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadTeamTickets}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {/* Loading State */}
      {((activeTab === 'team-tickets' && loading) || (activeTab === 'pending-assignments' && pendingLoading)) && (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">
              {activeTab === 'pending-assignments' ? 'Loading pending assignments...' : 'Loading team tickets...'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {/* {((activeTab === 'team-tickets' && !loading && filteredTickets.length === 0) || 
        (activeTab === 'pending-assignments' && !pendingLoading && filteredTickets.length === 0)) && (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {activeTab === 'pending-assignments' ? 'No pending assignments found' : 'No team tickets found'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {activeTab === 'pending-assignments'
                ? 'All open unassigned inquiries have been assigned'
                : (searchTerm || agentFilter !== 'all' || statusFilter !== 'all'
                    ? 'Try adjusting your search criteria'
                    : 'No tickets assigned to the team at the moment')
              }
            </p>
          </CardContent>
        </Card>
      )} */}

      <Modal
        isOpen={assignPriorityModalOpen}
        onClose={closeAssignPriorityModal}
        title="Assign Inquiry"
        size="md"
      >
        <div className="space-y-4">
          <div className="rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 p-3 space-y-1 text-sm">
            <div><span className="text-gray-500">Inquiry Category:</span> {assignTicket?.inquiryCategoryName || assignTicket?.category || '—'}</div>
            <div><span className="text-gray-500">Inquiry Sub Category:</span> {assignTicket?.inquirySubCategoryName || '—'}</div>
            <div><span className="text-gray-500">Escalation Level:</span> {assignEscalationLevelName || assignTicket?.escalationLevelName || 'Level 1'}</div>
          </div>

          <QueueConfidentialityLevelControl
            ticketId={assignTicket?.ticketId ?? assignTicket?.id}
            initialLevel={assignTicket?.confidentialityLevel}
            disabled={!!assigningTicketId}
            onSaved={({ removedFromQueue }) => {
              if (removedFromQueue) {
                closeAssignPriorityModal();
              }
              loadPendingAssignmentsRef.current?.();
            }}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Inquiry Priority Level <span className="text-red-500 font-semibold">*</span>
            </label>
            <select
              value={selectedAssignPriorityId}
              onChange={(e) => handleAssignPriorityChange(e.target.value)}
              disabled={!!assigningTicketId}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">Select inquiry priority...</option>
              {assignmentInquiryPriorities.map((priority) => {
                const pid = priority.id || priority.inquiryPriorityLevelId;
                return (
                  <option key={pid} value={String(pid)}>
                    {priority.priorityLevelName || priority.name}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Responsible Role
            </label>
            <input
              type="text"
              readOnly
              value={assignResponsibleRoleName || (selectedAssignPriorityId ? '—' : 'Select Inquiry Priority first')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              User <span className="text-red-500 font-semibold">*</span>
            </label>
            <select
              value={selectedAssignUserId}
              onChange={(e) => setSelectedAssignUserId(e.target.value)}
              disabled={loadingAssignUsers || !assignResponsibleRoleId}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">
                {loadingAssignUsers
                  ? 'Loading users...'
                  : !selectedAssignPriorityId
                    ? 'Select Inquiry Priority first'
                    : 'Select user...'}
              </option>
              {assignUsers.map((user) => {
                const id = user.userId || user.id;
                const name = user.fullName || user.username || user.email || `User ${id}`;
                return (
                  <option key={id} value={String(id)}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Assignment Remarks <span className="text-red-500 font-semibold">*</span>
            </label>
            <textarea
              value={assignmentRemarks}
              onChange={(e) => setAssignmentRemarks(e.target.value)}
              rows={3}
              placeholder="Enter assignment remarks..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="button"
            onClick={handleAssignInquiry}
            disabled={
              !selectedAssignPriorityId
              || !selectedAssignUserId
              || !assignmentRemarks.trim()
              || !!assigningTicketId
              || loadingAssignUsers
            }
            className="w-full px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            <UserPlus className="h-5 w-5 shrink-0" />
            {assigningTicketId ? 'Assigning…' : 'Assign Inquiry'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default TeamTickets;
